import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from "react";

function read<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

export function useLocalStorage<T>(key: string, initial: T | (() => T)): [T, Dispatch<SetStateAction<T>>, () => void] {
  const [value, setValue] = useState<T>(() => read(key, typeof initial === "function" ? (initial as () => T)() : initial));

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* quota or private mode */
    }
  }, [key, value]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== key || e.storageArea !== window.localStorage) return;
      setValue(e.newValue === null ? (typeof initial === "function" ? (initial as () => T)() : initial) : (JSON.parse(e.newValue) as T));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [key, initial]);

  const remove = useCallback(() => {
    window.localStorage.removeItem(key);
    setValue(typeof initial === "function" ? (initial as () => T)() : initial);
  }, [key, initial]);

  return [value, setValue, remove];
}
